import Vue from 'vue'
import Vuex from 'vuex'
import VuexPersist from 'vuex-persist'
// Modules
import userData from './modules/User'
import dashboard from './modules/Dashboard'
import challenge from './modules/Challenge'
import bookReading from './modules/BookReading'
import bookCatalog from './modules/BookCatalog'
import common from './modules/Common'
import snackbar from './modules/Snackbar'
import events from './modules/Events'

import ICommit from '@/store/contracts/commit'
// TODO

import getters from './getters'

Vue.use(Vuex)

// Wrap storage with QuotaExceededError handling
const safeStorage = {
    getItem: (key: string) => window.localStorage.getItem(key),
    setItem: (key: string, value: string) => {
        try {
            window.localStorage.setItem(key, value)
        } catch (e: any) {
            if (e?.name === 'QuotaExceededError' || e?.code === 22) {
                console.error('localStorage quota exceeded, clearing and retrying with minimal state')
                // Clear only our key to free space
                window.localStorage.removeItem('student-beereaders-app')
                // Retry with minimal state (just token/userId for re-login)
                try {
                    const minimalState = JSON.parse(value)
                    const reduced = JSON.stringify({
                        token: minimalState.token,
                        userData: minimalState.userData ? { user: { id: minimalState.userData.user?.id } } : undefined,
                    })
                    window.localStorage.setItem(key, reduced)
                } catch (retryError) {
                    console.error('Failed to persist even minimal state:', retryError)
                }
            } else {
                throw e
            }
        }
    },
    removeItem: (key: string) => window.localStorage.removeItem(key),
    clear: () => window.localStorage.clear(),
    get length() { return window.localStorage.length },
    key: (index: number) => window.localStorage.key(index),
}

const vuexPersist = new VuexPersist({
    // Al cambiar esta llave debe cambiarse también en "@/utils/request" para poder obtener el token
    key: 'student-beereaders-app',
    storage: safeStorage as Storage,
    reducer: (state: any) => ({
        token: state.token,
        categories: state.categories,
        userData: state.userData,
        // Exclude dashboard.testDiagnostic to prevent QuotaExceededError (diagnostic content can be ~2MB each)
        dashboard: state.dashboard ? {
            ...state.dashboard,
            testDiagnostic: undefined, // Don't persist heavy diagnostic content
        } : state.dashboard,
        common: state.common,
        // isMobile no se persiste porque debe calcularse en tiempo real basado en el tamaño de ventana
    }),
})

const defaultState: {
    categories: Array<any>
    loading: boolean
    token: string
    isValid: boolean
    rankingMode: string,
    isMobile: boolean
} = {
    categories: [],
    loading: false,
    token: '',
    isValid: false,
    rankingMode: '',
    isMobile: typeof window !== 'undefined' ? window.innerWidth < 600 : false,
}

const store = new Vuex.Store({
    state: defaultState,
    mutations: {
        categories: (state, value) => {
            state.categories = value
        },
        clearSession(state) {
            Object.assign(state, defaultState)
            setTimeout(() => {
                // Preserve school popup dismissal keys before clearing localStorage
                const schoolPopupKeys = Object.keys(localStorage).filter(key => key.startsWith('schoolPopup_dismissed_'))
                const preservedValues: { [key: string]: string } = {}
                schoolPopupKeys.forEach(key => {
                    preservedValues[key] = localStorage.getItem(key) || ''
                })

                vuexPersist.storage?.clear()

                // Restore preserved school popup keys
                Object.keys(preservedValues).forEach(key => {
                    localStorage.setItem(key, preservedValues[key])
                })
            }, 10)
        },
        loading: (state, value) => {
            state.loading = value
        },
        setToken: (state, value) => {
            state.token = value
        },
        rankingMode: (state: any, value: any) => {
            state.rankingMode = value
        },
        setIsMobile: (state: any, value: any) => {
            state.isMobile = value
        }
    },
    actions: {
        changeCategories({ commit }, categories) {
            commit('categories', categories)
        },
        clearSession({ commit }) {
            commit('clearSession')
        },
        setLoadingState({ commit }, value) {
            commit('loading', value)
        },
        setToken({ commit }, value) {
            commit('setToken', value)
        },
        isValidToken({ state }, value) {
            state.isValid = value
        },
        resetState({ state }) {
            Object.assign(state, defaultState)
        },
        setRankingMode({ commit }: ICommit, value: any) {
            commit('rankingMode', value)
        },
        setIsMobile({ commit }: ICommit, value: any) {
            commit('setIsMobile', value)
        }
    },
    modules: {
        userData,
        dashboard,
        bookReading,
        challenge,
        bookCatalog,
        common,
        snackbar,
        events
    },
    getters,
    plugins: [vuexPersist.plugin],
})

export default store
